import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { localStorageVariables } from '@/types/localStorageVariables';

interface LastStepContextType {
    lastStepMenuPath: string;
    setLastStepMenuPath: (menuPath: string) => void;
    currentStepMenuPath: string;
    setCurrentStepMenuPath: (menuPath: string) => void;
}

const LastStepContext = createContext<LastStepContextType | undefined>(undefined);

interface LastStepProviderProps {
    children: React.ReactNode;
}

// localStorage keys for navigation state
const LAST_STEP_MENU_PATH_KEY = 'lastStepMenuPath';
const CURRENT_STEP_MENU_PATH_KEY = 'currentStepMenuPath';

export const LastStepProvider: React.FC<LastStepProviderProps> = ({ children }) => {
    const [lastStepMenuPath, setLastStepMenuPathState] = useState<string>('');
    const [currentStepMenuPath, setCurrentStepMenuPathState] = useState<string>('');

    // Load initial values from localStorage on mount
    useEffect(() => {
        const savedLastStep = localStorage.getItem(LAST_STEP_MENU_PATH_KEY) || '';
        const savedCurrentStep = localStorage.getItem(CURRENT_STEP_MENU_PATH_KEY) || '';
        
        setLastStepMenuPathState(savedLastStep);
        setCurrentStepMenuPathState(savedCurrentStep);
    }, []);

    const setLastStepMenuPath = useCallback((path: string) => {
        setLastStepMenuPathState(path);
        localStorage.setItem(LAST_STEP_MENU_PATH_KEY, path);
    }, []);

    const setCurrentStepMenuPath = useCallback((path: string) => {
        setCurrentStepMenuPathState(path);
        localStorage.setItem(CURRENT_STEP_MENU_PATH_KEY, path);
    }, []);

    const contextValue: LastStepContextType = {
        lastStepMenuPath,
        setLastStepMenuPath,
        currentStepMenuPath,
        setCurrentStepMenuPath
    };

    return (
        <LastStepContext.Provider value={contextValue}>
            {children}
        </LastStepContext.Provider>
    );
};

export const useLastStepContext = (): LastStepContextType => {
    const context = useContext(LastStepContext);
    if (!context) {
        throw new Error('useLastStep must be used within a LastStepProvider');
    }
    return context;
};

// Constants for document setup states
export const LastStepStates = {
    AVAILABLE: true,
    NOT_AVAILABLE: false,
    BEFORE_APP_FEE_AVAILABLE: true,
    BEFORE_APP_FEE_NOT_AVAILABLE: false
} as const; 